import { useState, type ChangeEvent } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card.tsx";
import { Input } from "./ui/input.tsx";

/* Ported from shams-web src/components/TorqueFinder.tsx. The wrench data lives in code. */

interface WrenchSpec {
  range: number[];
  increment: number;
  brand: string;
  socket: string;
  unit: string;
}

const WRENCHES: WrenchSpec[] = [
  {
    range: [2.26, 3.95, 5.65, 7.34, 9.03, 10.7, 12.4, 14.1, 15.8, 17.5, 19.2, 20.9, 22.6],
    increment: 0.11,
    brand: "EPAuto",
    socket: '1/4"',
    unit: "N/m",
  },
  {
    range: [13.6, 20.3, 27.1, 33.9, 40.7, 47.5, 54.2, 61.0, 67.8, 74.6, 81.4, 88.1, 94.9, 101.7, 108.5],
    increment: 1.36,
    brand: "EPAuto",
    socket: '3/8"',
    unit: "N/m",
  },
  {
    range: [
      33.9, 47.5, 61.0, 74.6, 88.1, 101.7, 115.2, 128.8, 142.4, 155.9, 169.5, 183.0, 196.6, 210.2,
      223.7, 237.3, 250.8, 264.4, 277.9, 291.5, 305.1, 318.6, 332.2, 345.7,
    ],
    increment: 1.35,
    brand: "DEWALT",
    socket: '1/2"',
    unit: "N/m",
  },
];

/** Base setting below the target and the turns needed to reach it; null when out of range. */
const findSetting = (range: number[], increment: number, target: number) => {
  for (let i = 0; i < range.length - 1; i++) {
    if (target >= range[i] && target < range[i + 1]) {
      const turns = (target - range[i]) / increment;
      return { min: range[i], turns: Math.round(turns), turnsApprox: turns.toFixed(1) };
    }
  }
  return null;
};

const describe = (torque: number) =>
  WRENCHES.map((wrench) => {
    const result = findSetting(wrench.range, wrench.increment, torque);
    return result
      ? `${wrench.brand} ${wrench.socket}: ${result.min} ${wrench.unit} + ${result.turns} (~${result.turnsApprox}) turns = ${torque} ${wrench.unit}`
      : `${wrench.brand} ${wrench.socket}: Torque ${torque} ${wrench.unit} not supported`;
  });

export default function TorqueCalculator() {
  const [torque, setTorque] = useState(0);
  const results = torque > 0 ? describe(torque) : [];

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Torque Wrench Calculator</CardTitle>
          <CardDescription>Enter desired torque to calculate wrench settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="torque" className="text-sm font-medium">
              Target Torque (N/m)
            </label>
            <Input
              type="number"
              id="torque"
              value={torque || ""}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setTorque(parseFloat(event.target.value) || 0)
              }
              placeholder="Enter torque value"
              min="0"
              step="0.1"
            />
          </div>

          {results.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Results:</h3>
              <ul className="space-y-2">
                {results.map((item) => (
                  <li key={item} className="rounded-md bg-muted p-3 text-sm">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
